import React, { useEffect, useState } from "react";
import { subscribe, closeLightbox } from "../../lib/lightbox-store";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Global fullscreen image viewer. Mounted once in Layout and reacts to
 * openLightbox(images, index) calls from anywhere.
 */
export default function LightboxHost() {
  const [{ open, images, index }, setState] = useState({
    open: false,
    images: [],
    index: 0,
  });

  useEffect(() => subscribe(setState), []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") setState((s) => ({ ...s, index: (s.index + 1) % s.images.length }));
      if (e.key === "ArrowLeft") setState((s) => ({ ...s, index: (s.index - 1 + s.images.length) % s.images.length }));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open || !images.length) return null;
  const current = images[index] || images[0];
  const prev = () =>
    setState((s) => ({ ...s, index: (s.index - 1 + s.images.length) % s.images.length }));
  const next = () =>
    setState((s) => ({ ...s, index: (s.index + 1) % s.images.length }));

  return (
    <div
      data-testid="lightbox"
      role="dialog"
      aria-modal="true"
      onClick={closeLightbox}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0a0a0a]/95 px-4 py-8 backdrop-blur-sm"
    >
      <button
        type="button"
        data-testid="lightbox-close"
        aria-label="Close"
        onClick={(e) => {
          e.stopPropagation();
          closeLightbox();
        }}
        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20"
      >
        <X className="h-5 w-5" />
      </button>

      {images.length > 1 && (
        <>
          <button
            type="button"
            data-testid="lightbox-prev"
            aria-label="Previous"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2.5 text-white transition hover:bg-white/20 sm:left-6"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            data-testid="lightbox-next"
            aria-label="Next"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2.5 text-white transition hover:bg-white/20 sm:right-6"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}

      <figure
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-full max-w-5xl flex-col items-center"
      >
        <img
          src={current.url || current.thumbnail_url}
          alt={current.title || "Portfolio image"}
          data-testid="lightbox-image"
          className="max-h-[80vh] w-auto rounded-md object-contain"
        />
        <figcaption className="mt-4 flex items-center gap-3 text-xs uppercase tracking-[0.28em] text-white/60">
          <span data-testid="lightbox-counter">
            {index + 1} / {images.length}
          </span>
          {current.title ? <span className="text-white/80">— {current.title}</span> : null}
        </figcaption>
      </figure>
    </div>
  );
}
